'use client';

import { useState, useEffect } from 'react';
import styles from './ShareButtons.module.css';

interface ShareLabels {
  shareNative: string;
  shareFacebook: string;
  shareX: string;
  shareLinkedIn: string;
  shareWhatsApp: string;
  shareEmail: string;
  copyLink: string;
  linkCopied: string;
  shareEmailIntro: string;
  shareEmailReadMore: string;
  shareWhatsAppIntro: string;
  shareXSuffix: string;
}

interface ShareButtonsProps {
  title: string;
  description?: string;
  url: string;
  siteUrl?: string;
  shareLabel: string;
  labels?: Partial<ShareLabels>;
}

export function ShareButtons({ title, description, url, siteUrl, shareLabel, labels }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    setCanNativeShare(typeof navigator !== 'undefined' && !!navigator.share);
  }, []);

  const getFullUrl = () => {
    if (url.startsWith('http')) return url;
    if (siteUrl) return `${siteUrl}${url}`;
    if (typeof window === 'undefined') return url;
    return `${window.location.origin}${url}`;
  };

  const openShareLink = (shareUrl: string) => {
    const link = document.createElement('a');
    link.href = shareUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer nofollow';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleNativeShare = async () => {
    const fullUrl = getFullUrl();
    try {
      await navigator.share({
        title,
        text: description || title,
        url: fullUrl,
      });
    } catch (_) {
      // User cancelled
    }
  };

  const shareOnFacebook = () => {
    const fullUrl = getFullUrl();
    openShareLink(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}&quote=${encodeURIComponent(title)}`
    );
  };

  const shareOnX = () => {
    const fullUrl = getFullUrl();
    const suffix = labels?.shareXSuffix || '';
    const text = suffix ? `${title} ${suffix}` : title;
    openShareLink(
      `https://x.com/intent/tweet?url=${encodeURIComponent(fullUrl)}&text=${encodeURIComponent(text)}`
    );
  };

  const shareOnLinkedIn = () => {
    const fullUrl = getFullUrl();
    openShareLink(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`
    );
  };

  const shareOnWhatsApp = () => {
    const fullUrl = getFullUrl();
    const intro = labels?.shareWhatsAppIntro || '';
    const desc = description ? `\n\n${description}` : '';
    const text = `${intro}\n\n*${title}*${desc}\n\n${fullUrl}`;
    openShareLink(`https://wa.me/?text=${encodeURIComponent(text)}`);
  };

  const shareViaEmail = () => {
    const fullUrl = getFullUrl();
    const intro = labels?.shareEmailIntro || 'I thought you might find this interesting:';
    const readMore = labels?.shareEmailReadMore || 'Read the full article here:';
    const desc = description ? `\n\n${description}` : '';
    const body = `${intro}\n\n${title}${desc}\n\n${readMore}\n${fullUrl}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
  };

  const copyToClipboard = async () => {
    const fullUrl = getFullUrl();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = fullUrl;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Fallback failed
    }
  };

  return (
    <div className={styles.shareContainer}>
      <p className={styles.label}>{shareLabel}</p>
      <div className={styles.buttons}>
        {canNativeShare && (
          <button
            onClick={handleNativeShare}
            className={`${styles.button} ${styles.nativeShare}`}
            aria-label={labels?.shareNative || 'Share'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
          </button>
        )}

        <button
          onClick={shareOnFacebook}
          className={styles.button}
          aria-label={labels?.shareFacebook || 'Share on Facebook'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </button>

        <button
          onClick={shareOnWhatsApp}
          className={styles.button}
          aria-label={labels?.shareWhatsApp || 'Share via WhatsApp'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </button>

        <button
          onClick={shareOnX}
          className={styles.button}
          aria-label={labels?.shareX || 'Share on X'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </button>

        <button
          onClick={shareOnLinkedIn}
          className={styles.button}
          aria-label={labels?.shareLinkedIn || 'Share on LinkedIn'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        </button>

        <button
          onClick={shareViaEmail}
          className={styles.button}
          aria-label={labels?.shareEmail || 'Share via email'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
        </button>

        <button
          onClick={copyToClipboard}
          className={`${styles.button} ${copied ? styles.copied : ''}`}
          aria-label={copied ? (labels?.linkCopied || 'Link copied!') : (labels?.copyLink || 'Copy link')}
        >
          {copied ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
